import { z } from "zod";

/**
 * Validation for fsmDef form fields.
 *
 * The `validationRules` array on a field is written by hand by humans who do not
 * know Zod. This builder turns that flat, declarative list into a Zod schema while
 * staying forgiving: unknown rules, incompatible rules, invalid regexes and missing
 * messages must never crash — they fall back gracefully.
 */

/** Field nature. Optional, defaults to "string". */
export type ValidationFieldType = "string" | "number" | "date" | "boolean" | "email" | "url";

/** Name of a rule from the allow-list. Kept as a plain string in the input because
 * humans may write anything; unknown values are filtered out with a warning. */
export type ValidationRuleName =
  | "type"
  | "required"
  | "minLength"
  | "maxLength"
  | "min"
  | "max"
  | "pattern"
  | "startsWith";

/** A localized message, indexed by locale (e.g. `fr_FR`, `en_EN`). */
export type LocalizedMessage = Record<string, string>;

export type ValidationRuleParams = {
  /** Catalogue value: the type name, a length/bound, a prefix or a regex source. */
  value?: string | number | boolean;
  /** Regex flags, only for the `pattern` rule. */
  flags?: string;
};

/** A single human-authored rule. `params` and `message` are optional per rule. */
export type ValidationRule = {
  rule: string;
  params?: ValidationRuleParams;
  message?: LocalizedMessage;
};

const TYPE_RULE = "type";
const REQUIRED_RULE = "required";

const KNOWN_RULES: ReadonlySet<string> = new Set<ValidationRuleName>([
  "type",
  "required",
  "minLength",
  "maxLength",
  "min",
  "max",
  "pattern",
  "startsWith",
]);

const VALID_TYPES: ReadonlySet<string> = new Set<ValidationFieldType>([
  "string",
  "number",
  "date",
  "boolean",
  "email",
  "url",
]);

const TEXT_TYPES: ReadonlySet<string> = new Set<ValidationFieldType>(["string", "email", "url"]);

const WARN_PREFIX = "[buildZodFromRules]";

/**
 * Resolve a localized message with the cascade:
 *   message[currentLocale] -> message[defaultLocale] -> first available -> undefined.
 *
 * Returning `undefined` is intentional: it tells Zod to keep its own default message.
 */
export const resolveMessage = (
  message: LocalizedMessage | undefined,
  currentLocale: string,
  defaultLocale = "fr_FR"
): string | undefined =>
  message?.[currentLocale] ?? message?.[defaultLocale] ?? Object.values(message ?? {})[0];

const numericParam = (rule: ValidationRule): number | undefined => {
  const raw = rule.params?.value;
  if (raw === undefined || raw === null || typeof raw === "boolean") return undefined;
  const num = Number(raw);
  return Number.isNaN(num) ? undefined : num;
};

/** "" / null / undefined -> undefined so that `required` / `optional` see an absent value. */
const emptyToUndefined = (value: unknown): unknown =>
  value === "" || value === null ? undefined : value;

/**
 * Coerce form input for number fields without `z.coerce.number()`'s "" -> 0 footgun:
 *   - "" / null / undefined            -> undefined  (so `required` can fire)
 *   - non-numeric string               -> kept as-is (so the type message can fire)
 *   - numeric string / number          -> Number(x)
 */
const numberPreprocess = (value: unknown): unknown => {
  if (value === "" || value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const num = Number(trimmed);
    return Number.isNaN(num) ? value : num;
  }
  return value;
};

/** Same idea as `numberPreprocess` but for dates coming from `<input type="date">`. */
const datePreprocess = (value: unknown): unknown => {
  if (value === "" || value === null || value === undefined) return undefined;
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date;
  }
  return value;
};

const warnIgnored = (rule: string, reason: string): void => {
  // eslint-disable-next-line no-console
  console.warn(`${WARN_PREFIX} rule "${rule}" ignored: ${reason}.`);
};

const resolveFieldType = (typeRule: ValidationRule | undefined): ValidationFieldType => {
  const value = typeRule?.params?.value;
  if (value === undefined) return "string";
  if (typeof value === "string" && VALID_TYPES.has(value)) return value as ValidationFieldType;
  // eslint-disable-next-line no-console
  console.warn(`${WARN_PREFIX} unknown type "${String(value)}" ignored, using "string".`);
  return "string";
};

const NOT_TEXT = "field is not text";
const NOT_NUMERIC = "field is not a number or date";

const applyTextRefinement = (
  schema: z.ZodString,
  rule: ValidationRule,
  message: string | undefined
): z.ZodString => {
  switch (rule.rule) {
    case "minLength": {
      const value = numericParam(rule);
      return value === undefined ? schema : schema.min(value, message);
    }
    case "maxLength": {
      const value = numericParam(rule);
      return value === undefined ? schema : schema.max(value, message);
    }
    case "startsWith":
      return schema.startsWith(String(rule.params?.value ?? ""), message);
    case "pattern": {
      try {
        const regex = new RegExp(String(rule.params?.value ?? ""), rule.params?.flags);
        return schema.regex(regex, message);
      } catch {
        // eslint-disable-next-line no-console
        console.warn(`${WARN_PREFIX} invalid pattern ignored: "${String(rule.params?.value)}".`);
        return schema;
      }
    }
    case "min":
    case "max":
      warnIgnored(rule.rule, NOT_TEXT);
      return schema;
    default:
      return schema;
  }
};

const applyNumberRefinement = (
  schema: z.ZodNumber,
  rule: ValidationRule,
  message: string | undefined
): z.ZodNumber => {
  switch (rule.rule) {
    case "min": {
      const value = numericParam(rule);
      return value === undefined ? schema : schema.min(value, message);
    }
    case "max": {
      const value = numericParam(rule);
      return value === undefined ? schema : schema.max(value, message);
    }
    case "minLength":
    case "maxLength":
    case "pattern":
    case "startsWith":
      warnIgnored(rule.rule, NOT_NUMERIC);
      return schema;
    default:
      return schema;
  }
};

const applyDateRefinement = (
  schema: z.ZodDate,
  rule: ValidationRule,
  message: string | undefined
): z.ZodDate => {
  switch (rule.rule) {
    case "min":
    case "max": {
      const raw = rule.params?.value;
      const date = raw === undefined ? undefined : new Date(raw as string | number);
      if (!date || Number.isNaN(date.getTime())) {
        warnIgnored(rule.rule, "value is not a valid date");
        return schema;
      }
      return rule.rule === "min" ? schema.min(date, message) : schema.max(date, message);
    }
    case "minLength":
    case "maxLength":
    case "pattern":
    case "startsWith":
      warnIgnored(rule.rule, NOT_NUMERIC);
      return schema;
    default:
      return schema;
  }
};

type BuildContext = {
  typeMessage: string | undefined;
  requiredMessage: string | undefined;
  isRequired: boolean;
  refinements: ValidationRule[];
  resolve: (message: LocalizedMessage | undefined) => string | undefined;
};

const buildTextSchema = (fieldType: ValidationFieldType, ctx: BuildContext): z.ZodTypeAny => {
  let base = z.string({
    required_error: ctx.requiredMessage,
    invalid_type_error: ctx.typeMessage,
  });

  // `.min(1)` before the format so the "required" message wins on an empty value.
  if (ctx.isRequired) base = base.min(1, ctx.requiredMessage);
  if (fieldType === "email") base = base.email(ctx.typeMessage);
  if (fieldType === "url") base = base.url(ctx.typeMessage);

  for (const rule of ctx.refinements) {
    base = applyTextRefinement(base, rule, ctx.resolve(rule.message));
  }

  if (ctx.isRequired) return base;
  // Optional + format/length: HTML sends "" which would trip the format. Map it away first.
  return z.preprocess(emptyToUndefined, base.optional());
};

const buildNumberSchema = (ctx: BuildContext): z.ZodTypeAny => {
  let inner = z.number({
    required_error: ctx.requiredMessage,
    invalid_type_error: ctx.typeMessage,
  });

  for (const rule of ctx.refinements) {
    inner = applyNumberRefinement(inner, rule, ctx.resolve(rule.message));
  }

  const schema = ctx.isRequired ? inner : inner.optional();
  return z.preprocess(numberPreprocess, schema);
};

const buildDateSchema = (ctx: BuildContext): z.ZodTypeAny => {
  let inner = z.date({
    required_error: ctx.requiredMessage,
    invalid_type_error: ctx.typeMessage,
  });

  for (const rule of ctx.refinements) {
    inner = applyDateRefinement(inner, rule, ctx.resolve(rule.message));
  }

  const schema = ctx.isRequired ? inner : inner.optional();
  return z.preprocess(datePreprocess, schema);
};

const buildBooleanSchema = (ctx: BuildContext): z.ZodTypeAny => {
  for (const rule of ctx.refinements) {
    warnIgnored(rule.rule, "field is a boolean");
  }

  const base = z.boolean({
    required_error: ctx.requiredMessage,
    invalid_type_error: ctx.typeMessage,
  });

  // A required boolean (e.g. a "I accept" checkbox) must be checked.
  if (ctx.isRequired) {
    return z.preprocess(
      (value) => (value === "" || value === null ? undefined : value),
      base.refine((value) => value === true, { message: ctx.requiredMessage })
    );
  }
  return base.optional();
};

/**
 * Build a Zod schema from a field's `validationRules`.
 *
 * The flat array is partitioned into three families that are assembled differently:
 *   - `type`        (at most 1) builds the base. `email`/`url` => `z.string().email()/.url()`.
 *   - `required`    (at most 1) is applied last.
 *   - refinements   (everything else) chain in array order — the first failure wins.
 */
export const buildZodFromRules = (
  rules: ValidationRule[] | undefined,
  currentLocale: string,
  defaultLocale = "fr_FR"
): z.ZodTypeAny => {
  const resolve = (message: LocalizedMessage | undefined) =>
    resolveMessage(message, currentLocale, defaultLocale);

  if (!rules || rules.length === 0) return z.any();

  const known = rules.filter((rule) => {
    if (KNOWN_RULES.has(rule.rule)) return true;
    // eslint-disable-next-line no-console
    console.warn(`${WARN_PREFIX} unknown rule ignored: "${rule.rule}".`);
    return false;
  });

  const typeRules = known.filter((rule) => rule.rule === TYPE_RULE);
  const requiredRules = known.filter((rule) => rule.rule === REQUIRED_RULE);
  const refinements = known.filter(
    (rule) => rule.rule !== TYPE_RULE && rule.rule !== REQUIRED_RULE
  );

  if (typeRules.length > 1) {
    throw new Error(
      `${WARN_PREFIX} multiple "type" rules defined (${typeRules.length}): a field can only have one type.`
    );
  }

  const fieldType = resolveFieldType(typeRules[0]);
  const ctx: BuildContext = {
    typeMessage: resolve(typeRules[0]?.message),
    requiredMessage: resolve(requiredRules[0]?.message),
    isRequired: requiredRules.length > 0,
    refinements,
    resolve,
  };

  if (TEXT_TYPES.has(fieldType)) return buildTextSchema(fieldType, ctx);
  if (fieldType === "number") return buildNumberSchema(ctx);
  if (fieldType === "date") return buildDateSchema(ctx);
  return buildBooleanSchema(ctx);
};
